import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { HttpClient, HttpEventType } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { MediaUploadService } from "src/app/Services/media-upload.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-flow-step-issues",
  templateUrl: "./flow-step-issues.component.html",
  styleUrls: ["./flow-step-issues.component.scss"],
})
export class FlowStepIssuesComponent implements OnInit, OnChanges {
  /** Rendered inside the step panel instead of a dialog. Uses the same issue fields and does not complete the step. */
  @Input() embedded = false;
  @Input() flowDataInput: any;
  @Input() lockedStepIndex: number | null = null;
  @Output() submittedIssue = new EventEmitter<any>();
  @Output() dismissed = new EventEmitter<void>();

  flowData: any;
  userData: any;
  contentType: "flow" | "bundle" = "flow";
  steps: any[] = [];

  // Currently selected step index from dropdown
  selectedStepIndex: number | null = null;

  // Temp issue being created for the selected step
  currentIssue: {
    description: string;
    mediaUrl: string;
    mediaThumbnailUrl: string;
    isVideo: boolean;
    issueType: string;
    equipment: string;
    location: string;
    urgency: string;
  } = {
    description: "",
    mediaUrl: "",
    mediaThumbnailUrl: "",
    isVideo: false,
    issueType: "",
    equipment: "",
    location: "",
    urgency: "Medium",
  };

  // Final list of issues that will be submitted
  issues: any[] = [];

  // Upload state for the currently edited issue
  isUploading = false;
  uploadProgress = 0;

  constructor(
    @Optional() public dialogRef: MatDialogRef<FlowStepIssuesComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private flowServiceService: FlowServiceService,
    private http: HttpClient,
    private toastr: ToastrService,
    private mediaUploadService: MediaUploadService
  ) {
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    if (data) {
      this.flowData = data.flowData;
      this.contentType = data?.contentType === "bundle" ? "bundle" : "flow";
      this.steps = this.getIssueTargetList();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.embedded) return;
    if (changes["flowDataInput"] && this.flowDataInput) {
      this.flowData = this.flowDataInput;
      this.steps = this.getIssueTargetList();
    }
    if (this.lockedStepIndex !== null && this.lockedStepIndex !== undefined) {
      this.selectedStepIndex = this.lockedStepIndex;
    }
  }

  get itemLabel(): string {
    return this.contentType === "bundle" ? "flow" : "step";
  }

  get itemLabelTitle(): string {
    return this.contentType === "bundle" ? "Flow" : "Step";
  }

  ngOnInit() {
    // No per-step initialization needed now; issues will be added via dropdown
  }

  async onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    const imageExtensions = [
      "jpeg", "jpg", "png", "gif", "jfif", "tiff", "tif", "pdf",
      "bmp", "webp", "svg", "heic", "heif", "raw", "arw", "cr2",
      "nef", "orf", "rw2", "dng", "psd", "ico", "exif", "pjpeg"
    ];
    const videoExtensions = [
      "mp4", "mov", "wmv", "flv", "avchd", "webm", "mkv", "vob",
      "ogv", "gifv", "m4p", "m4v", "3gp", "3g2", "avi", "mpg", "mpeg"
    ];

    const fileName = file.name.toLowerCase();
    const isImage = imageExtensions.some((ext) => fileName.includes(ext));
    const isVideo = videoExtensions.some((ext) => fileName.includes(ext));

    if (!isImage && !isVideo) {
      this.toastr.error("Please select an image or video file");
      this.isUploading = false;
      return;
    }

    let fileToUpload = file;
    if (isVideo) {
      try {
        fileToUpload = await this.mediaUploadService.prepareFileForUpload(file);
      } catch (error: any) {
        this.isUploading = false;
        this.toastr.error(error?.message || this.mediaUploadService.uploadTooLargeMessage());
        return;
      }
    }

    const formData = new FormData();
    formData.append("fileData", fileToUpload);

    const uploadUrl = isImage
      ? environment.apiBaseUrl + environment.uploadImageUrl
      : environment.apiBaseUrl + environment.uploadVideoUrl;

    this.http
      .post(uploadUrl, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe(
        (event: any) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                this.uploadProgress = Math.round(
                  (event.loaded / event.total) * 100
                );
              }
              break;

            case HttpEventType.Response:
              const mediaUploadStatus = event.body as any;
              this.isUploading = false;

              if (mediaUploadStatus != null && mediaUploadStatus.status) {
                const issue = this.currentIssue;
                if (isImage) {
                  issue.mediaUrl =
                    environment.imageStorageBaseUrl +
                    mediaUploadStatus.fileName;
                  issue.mediaThumbnailUrl = issue.mediaUrl;
                } else if (isVideo) {
                  issue.mediaUrl =
                    environment.videoStorageBaseUrl +
                    mediaUploadStatus.fileName;
                  issue.mediaThumbnailUrl = issue.mediaUrl;
                }
                issue.isVideo = isVideo;
                this.currentIssue = { ...issue };
                this.toastr.success("Media uploaded successfully");
              } else {
                this.toastr.error(mediaUploadStatus?.message || "Upload failed");
              }
              break;
          }
        },
        (error) => {
          this.isUploading = false;
          this.toastr.error(this.mediaUploadService.uploadTooLargeMessage());
        }
      );
  }

  removeMedia() {
    this.currentIssue = {
      ...this.currentIssue,
      mediaUrl: "",
      mediaThumbnailUrl: "",
      isVideo: false,
    };
  }

  hasIssues(): boolean {
    return this.issues.length > 0;
  }

  // Check if a step already has an issue added
  isStepAlreadyUsed(stepIndex: number): boolean {
    return this.issues.some((issue) => issue.stepIndex === stepIndex);
  }

  canAddIssue(): boolean {
    const hasStep = this.embedded || this.selectedStepIndex !== null;
    const hasContent =
      (this.currentIssue.description || "").trim().length > 0 ||
      !!this.currentIssue.mediaUrl;
    return hasStep && hasContent && !this.isUploading;
  }

  addIssue() {
    if (!this.canAddIssue()) {
      this.toastr.warning(`Please select a ${this.itemLabel} and add issue details`);
      return;
    }

    const index = this.selectedStepIndex as number;
    this.issues.push({
      stepIndex: index,
      stepTitle: this.steps[index]?.title || `${this.itemLabelTitle} ${index + 1}`,
      issueDescription: this.currentIssue.description,
      mediaUrl: this.currentIssue.mediaUrl || null,
      mediaThumbnailUrl: this.currentIssue.mediaThumbnailUrl || null,
      isVideo: this.currentIssue.isVideo || false,
      issueType: this.currentIssue.issueType || null,
      equipment: this.currentIssue.equipment || null,
      location: this.currentIssue.location || null,
      urgency: this.currentIssue.urgency || "Medium",
      resolutionStatus: "Open",
    });

    // Clear current issue description and media and reset selection
    this.currentIssue = {
      description: "",
      mediaUrl: "",
      mediaThumbnailUrl: "",
      isVideo: false,
      issueType: "",
      equipment: "",
      location: "",
      urgency: "Medium",
    };
    this.selectedStepIndex = null;
    this.uploadProgress = 0;
  }

  removeIssue(index: number) {
    this.issues.splice(index, 1);
  }

  submit() {
    if (this.embedded) {
      this.submitEmbedded();
      return;
    }
    if (!this.hasIssues()) {
      this.toastr.warning("Please add at least one issue");
      return;
    }

    this.dialogRef?.close({ issues: this.issues });
  }

  /** One issue for the current step. The step panel submits it through the existing review workflow. */
  submitEmbedded(): void {
    if (!this.canAddIssue()) {
      this.toastr.warning("Describe the issue or attach a photo before submitting.");
      return;
    }
    const index = this.selectedStepIndex;
    this.submittedIssue.emit({
      stepIndex: index,
      stepTitle:
        index !== null && this.steps[index]
          ? this.steps[index].title || `Step ${index + 1}`
          : "This step",
      issueDescription: this.currentIssue.description,
      mediaUrl: this.currentIssue.mediaUrl || null,
      mediaThumbnailUrl: this.currentIssue.mediaThumbnailUrl || null,
      isVideo: this.currentIssue.isVideo || false,
      issueType: this.currentIssue.issueType || null,
      equipment: this.currentIssue.equipment || null,
      location: this.currentIssue.location || null,
      urgency: this.currentIssue.urgency || "Medium",
      resolutionStatus: "Open",
    });
  }

  resetDraft(): void {
    this.currentIssue = {
      description: "",
      mediaUrl: "",
      mediaThumbnailUrl: "",
      isVideo: false,
      issueType: "",
      equipment: "",
      location: "",
      urgency: "Medium",
    };
    this.uploadProgress = 0;
  }

  skip() {
    // User chose to skip adding step-wise issues but continue to review
    this.dialogRef?.close({ issues: [], skip: true });
  }

  close() {
    if (this.embedded) {
      this.dismissed.emit();
      return;
    }
    this.dialogRef?.close({ issues: null });
  }

  private getIssueTargetList(): any[] {
    if (Array.isArray(this.flowData?.steps) && this.flowData.steps.length > 0) {
      return this.flowData.steps;
    }

    if (this.contentType === "bundle" && Array.isArray(this.flowData?.flows)) {
      return this.flowData.flows.map((flow: any, index: number) => ({
        title: flow?.title || flow?.name || `Flow ${index + 1}`,
      }));
    }

    return [];
  }
}


